﻿import { Checkbox, FormLabel, HStack, Select, Stack } from '@chakra-ui/react'
import { priceFormat } from 'BrokerTsLib/src/Utils/formatPrice'
import type { AdditionSmartTravel, OfferProperty, OfferPropertyAddition, PackageItemModel } from 'BrokerTsLib/src/dtos'
import { SmartTravelInsuredCounts } from 'BrokerTsLib/src/dtos'
import { useFieldArray, useFormContext } from 'BrokerTsLib/src/rhf'
import { useEffect } from 'react'

type SmartTravelProps = {
  offer: OfferProperty
  addon: PackageItemModel
}

// limity storna dané specifikací GČP
const cancellationLimits = [30000, 50000]

const getDefaultItem = (addon: PackageItemModel) => {
  const defaultVariant = addon.variants.find(x => x.isDefault) ?? addon.variants[0]

  return {
    packageItemId: addon.packageItemId,
    variantId: defaultVariant?.id,
    limit: defaultVariant?.defaultLimit?.toString(),
    riskId: addon.riskId,
    insuredCount: 'One',
    injury: false,
    cancellation: false,
  } as AdditionSmartTravel
}

function SmartTravel({
  offer,
  addon
}: SmartTravelProps) {

  const {
    control,
    register,
    setValue,
    watch
  } = useFormContext<OfferPropertyAddition>()

  const name = 'smartTravel'

  const { append, fields } = useFieldArray({
    control: control,
    name: name
  })

  // vychozi nastaveni hodnot na OfferPropertyAddition z OfferPart.
  // Po rekalkulaci nabidky muze byt offerPartId z AdditionXmlData neplatne - polozka se pak ukotvi
  // na aktualni part stejneho rizika, pripadne na addon (offerPartId = 0 -> na serveru se zalozi novy part)
  useEffect(() => {
    fields.forEach((item, index) => {
      const part = offer.parts.find(x => x.partId == item.offerPartId)
        ?? offer.parts.find(x => x.packageItem?.riskId == addon.riskId)

      setValue(`${name}.${index}.riskId`, part?.packageItem.riskId ?? addon.riskId)
      setValue(`${name}.${index}.limit`, part?.limit?.toString())
      setValue(`${name}.${index}.variantId`, part?.packageItemVariantId)
      setValue(`${name}.${index}.offerPartId`, part?.partId ?? 0)
      setValue(`${name}.${index}.packageItemId`, part?.packageItem.packageItemId ?? addon.packageItemId)
    })

    if (fields.length == 0)
      append(getDefaultItem(addon))
  }, [])

  const cancellation = watch(`${name}.0.cancellation`)

  const handleChangeCancellation = (checked: boolean) => {
    if (checked) {
      setValue(`${name}.0.cancellationLimit`, cancellationLimits[0])
      setValue(`${name}.0.cancellationDeductible`, 0)
    } else {
      setValue(`${name}.0.cancellationLimit`, null)
      setValue(`${name}.0.cancellationDeductible`, null)
    }
  }

  if (fields.length == 0) return null

  return (
    <HStack gap={8} alignItems='start'>
      <Stack minW='140px'>
        <FormLabel m={0}>Počet pojištěných osob</FormLabel>

        <Select
          size='sm'
          sx={{ width: '100%' }}
          {...register(`${name}.0.insuredCount`)}
        >
          {Object.keys(SmartTravelInsuredCounts).map(key => {
            const value = SmartTravelInsuredCounts[key as keyof typeof SmartTravelInsuredCounts]
            return (
              <option key={key} value={key}>
                {value}
              </option>
            )
          })}
        </Select>
      </Stack>

      <Stack pt={7}>
        <Checkbox
          size='sm'
          {...register(`${name}.0.injury`)}
        >
          Úrazové pojištění
        </Checkbox>

        <Checkbox
          size='sm'
          {...register(`${name}.0.cancellation`, {
            onChange: (e) => handleChangeCancellation(e.target.checked)
          })}
        >
          Pojištění storna
        </Checkbox>
      </Stack>

      {cancellation &&
        <>
          <Stack minW='120px'>
            <FormLabel m={0}>Limit storna</FormLabel>

            <Select
              size='sm'
              sx={{ width: '100%' }}
              {...register(`${name}.0.cancellationLimit`)}
            >
              {cancellationLimits.map(limit => (
                <option key={limit} value={limit.toString()}>
                  {priceFormat(limit)}
                </option>
              ))}
            </Select>
          </Stack>

          <Stack minW='100px'>
            <FormLabel m={0}>Spoluúčast</FormLabel>

            <Select
              size='sm'
              sx={{ width: '100%' }}
              {...register(`${name}.0.cancellationDeductible`)}
            >
              <option value='0'>0 %</option>
              <option value='20'>20 %</option>
            </Select>
          </Stack>
        </>
      }
    </HStack>
  )
}

export default SmartTravel
